import * as React from 'react';
import { ReactGridContainer } from '@europrocurement/flexy-components';
import { DossierOpenSearchJsonldOpensearchDossierStatutRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import {
    DOSSIER_STATUS_ARCHIVED,
    DOSSIER_STATUS_DONE,
    DOSSIER_STATUS_DRAFT_CANCELLED,
    DOSSIER_STATUS_SUSPENDED,
    DOSSIER_STATUS_TRANSMITTED_CANCELLED,
    DOSSIER_STATUS_VALIDATED,
    getDossierStatus,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { faFolder } from '@fortawesome/sharp-regular-svg-icons';
import { Box, Typography, useTheme } from '@mui/material';
import { type ResizeHandleAxis } from 'react-grid-layout';
import { useStatusStyle } from '@europrocurement/l2d-modules/hooks';
import WidgetWithAction from '../molecules/WidgetWithAction/WidgetWithAction';
import DashboardStatCardTotalBar from '../molecules/DashboardStatCardTotalBar/DashboardStatCardTotalBar';

type DossierStatusWidgetDashboardProps = {
    statusDossierOpenSearch: DossierOpenSearchJsonldOpensearchDossierStatutRead[];
    handleDossierStatusClick: (statutId?: number | null) => void;
    showResetButton?: boolean;
};

const RESIZE_HANDLES = ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] as ResizeHandleAxis[];

const DossierStatusWidgetDashboard: React.FunctionComponent<DossierStatusWidgetDashboardProps> =
    function ({ statusDossierOpenSearch = [], handleDossierStatusClick, showResetButton }) {
        const { palette } = useTheme();
        const { getDossierStatusStyle } = useStatusStyle();
        const [statusDossiers, setStatusDossiers] = React.useState<
            DossierOpenSearchJsonldOpensearchDossierStatutRead[]
        >(
            getDossierStatus().filter(
                (dossStatus) =>
                    ![
                        DOSSIER_STATUS_SUSPENDED,
                        DOSSIER_STATUS_ARCHIVED,
                        DOSSIER_STATUS_DRAFT_CANCELLED,
                        DOSSIER_STATUS_TRANSMITTED_CANCELLED,
                        DOSSIER_STATUS_DONE,
                        DOSSIER_STATUS_ARCHIVED,
                        DOSSIER_STATUS_VALIDATED,
                    ].includes(dossStatus.id as number),
            ),
        );

        React.useEffect(() => {
            setStatusDossiers(
                getDossierStatus()
                    .filter(
                        (dossStatus) =>
                            ![
                                DOSSIER_STATUS_SUSPENDED,
                                DOSSIER_STATUS_ARCHIVED,
                                DOSSIER_STATUS_DRAFT_CANCELLED,
                                DOSSIER_STATUS_TRANSMITTED_CANCELLED,
                                DOSSIER_STATUS_DONE,
                                DOSSIER_STATUS_ARCHIVED,
                                DOSSIER_STATUS_VALIDATED,
                            ].includes(dossStatus.id as number),
                    )
                    .map((statusDossier) => ({
                        ...statusDossier,
                        childsCount: statusDossierOpenSearch.find(
                            (statusOpenSearch) => statusOpenSearch.id === statusDossier.id,
                        )
                            ? statusDossierOpenSearch.find(
                                  (statusOpenSearch) => statusOpenSearch.id === statusDossier.id,
                              )?.childsCount
                            : statusDossier.childsCount,
                    })),
            );
        }, [statusDossierOpenSearch]);

        // Pure layout computation — recap card at x=0..2, status items fill x=3..5 in row 0 then full rows
        const statusLayoutProps = statusDossiers.map((statusDossier, i) => ({
            i: `dossier_status_${(statusDossier.id as number).toString()}`,
            x: i < 3 ? 3 + i : (i - 3) % 6,
            y: i < 3 ? 0 : 1 + Math.floor((i - 3) / 6),
            w: 1,
            h: 1,
            isResizable: true,
            resizeHandles: RESIZE_HANDLES,
        }));

        return (
            <ReactGridContainer
                localStorageName="grid-status-dossier-formalist"
                gridconfig={{ cols: 6, rowHeight: 200 }}
                showResetButton={showResetButton}
                layoutProps={[
                    {
                        i: 'dossier_status_recap',
                        x: 0,
                        y: 0,
                        w: 3,
                        h: 1,
                        isResizable: true,
                        resizeHandles: RESIZE_HANDLES,
                    },
                    ...statusLayoutProps,
                ]}
            >
                {(() => (
                    <Box
                        key="dossier_status_recap"
                        sx={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: palette.background.paper,
                            borderRadius: 2,
                            boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                        }}
                    >
                        <DashboardStatCardTotalBar
                            items={statusDossiers.map((st) => ({
                                ...st,
                                key: `${st.id}_${st.stepId}`,
                            }))}
                            title="Suivi dossier — flux actif"
                            icon={faFolder}
                            handleItemClick={handleDossierStatusClick}
                            handleStyleStatus={getDossierStatusStyle}
                        />
                    </Box>
                ))()}

                {statusDossiers.map((statusDossier) => {
                    const { paletteString, namedIcon } = getDossierStatusStyle(
                        statusDossier.id as number,
                    );
                    return (
                        <Box
                            key={`dossier_status_${(statusDossier.id as number).toString()}`}
                            sx={{ height: '100%' }}
                        >
                            <WidgetWithAction
                                title={
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                            variant="l2dh6"
                                            fontWeight="bold"
                                        >
                                            {statusDossier.libelle}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color={palette.text.secondary}
                                        >
                                            {statusDossier.stepLabel}
                                        </Typography>
                                    </Box>
                                }
                                icon={namedIcon}
                                actionMessage="Voir les dossiers"
                                actionCallback={async () =>
                                    handleDossierStatusClick(statusDossier.id as number)
                                }
                                data={statusDossier.childsCount ?? '0'}
                                color={paletteString}
                            />
                        </Box>
                    );
                })}
            </ReactGridContainer>
        );
    };

export default DossierStatusWidgetDashboard;
